import { z } from 'zod';
import { patchOf } from './zod-setup';
import { addressSchema, currencyCodeSchema } from './common';
import { LIST_STATUSES } from './items';
import { isValidGstinChecksum, stateOfGstin } from './org-settings';
import { advancedFilterQueryFragment } from './record-filters';
/**
 * Customers and vendors.
 *
 * They share a schema because they are the same shape - a party you trade with,
 * with addresses, a currency, payment terms and a tax registration. Zoho keeps
 * them as one "contact" record for the same reason, and plenty of small
 * businesses have a counterparty that is both.
 */
/** How a counterparty is treated for GST. Zoho's names, kept deliberately. */
export const GST_TREATMENTS = [
    'business_gst',
    'business_none',
    'consumer',
    'overseas',
    'sez',
    'sez_developer',
    'deemed_export',
];
export const GST_TREATMENT_LABELS = {
    business_gst: 'Registered business',
    business_none: 'Unregistered business',
    consumer: 'Consumer',
    overseas: 'Overseas',
    sez: 'SEZ',
    sez_developer: 'SEZ developer',
    deemed_export: 'Deemed export',
};
/** Treatments that require a GSTIN to be meaningful. */
export const GST_TREATMENTS_NEEDING_GSTIN = [
    'business_gst',
    'sez',
    'sez_developer',
    'deemed_export',
];
export const partyContactSchema = z
    .object({
    id: z.string().trim().max(64).optional(),
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    mobile: z.string().trim().max(40).optional(),
    designation: z.string().trim().max(80).optional(),
    /** Primary contacts are cc'd on every document sent to this party. */
    isPrimary: z.boolean().optional(),
})
    .strict();
const partyBase = z.object({
    /**
     * What appears on documents and in every picker. Separate from company name
     * because "Acme Pvt Ltd" is the legal name and "Acme (Chennai)" is what the
     * person raising the invoice actually searches for.
     */
    displayName: z.string().trim().min(1, 'Give this customer a display name').max(200),
    companyName: z.string().trim().max(200).optional(),
    email: z.string().trim().toLowerCase().email('Enter a valid email').optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    website: z.string().trim().url().max(200).optional().or(z.literal('')),
    billingAddress: addressSchema.optional(),
    shippingAddress: addressSchema.optional(),
    /** The currency invoices to this customer start in. Each invoice can still choose. */
    currency: currencyCodeSchema.optional(),
    /** Overrides the org default for documents raised against this party. */
    paymentTermId: z.string().trim().max(64).nullable().optional(),
    gstTreatment: z.enum(GST_TREATMENTS).optional(),
    gstin: z.string().trim().toUpperCase().max(15).optional().or(z.literal('')),
    /** Their PAN, printed on TDS documents. */
    pan: z.string().trim().toUpperCase().max(10).optional().or(z.literal('')),
    /**
     * Where supply is made TO, defaulted onto every document for this party.
     * Kept on the party as well as the document because a customer's billing
     * state is stable while a document's place of supply can legitimately differ.
     */
    placeOfSupply: z.string().trim().toUpperCase().max(4).optional(),
    /** Their VAT/TRN/ABN, for orgs not on GST. */
    taxRegistrationNumber: z.string().trim().max(40).optional(),
    contacts: z.array(partyContactSchema).max(25).optional(),
    notes: z.string().trim().max(4000).optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
    /**
     * Opening balance: what they already owed when the books moved into Fint.
     * Positive means they owe you.
     */
    openingBalance: z.string().trim().optional(),
});
/**
 * A counterparty's GSTIN, checked the same way the org's own is.
 *
 * Not just "is one present". A customer's GSTIN prints on their invoice and is
 * what lets them claim input tax credit - a typo'd one is discovered by their
 * accountant, months later, and the invoice has to be reissued. The checksum
 * catches every single-character typo, so it is worth running here too.
 */
function validateGstin(v, ctx) {
    const needsGstin = v.gstTreatment && GST_TREATMENTS_NEEDING_GSTIN.includes(v.gstTreatment);
    if (needsGstin && !v.gstin) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['gstin'],
            message: 'This GST treatment needs a GSTIN. It prints on their invoices and they cannot claim credit without it.',
        });
        return;
    }
    if (!v.gstin)
        return;
    if (!isValidGstinChecksum(v.gstin)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['gstin'],
            message: 'That GSTIN fails its checksum - check for a typo. It prints on every invoice you send them.',
        });
        return;
    }
    // A GSTIN carries its own state in the first two digits, so a place of supply
    // that disagrees with it means one of the two is wrong - and either way the
    // CGST/SGST vs IGST split would come out wrong on every invoice.
    const issuedIn = stateOfGstin(v.gstin);
    if (issuedIn && v.placeOfSupply && issuedIn.code !== v.placeOfSupply) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['placeOfSupply'],
            message: `That GSTIN was issued in ${issuedIn.name}, but the place of supply says ${v.placeOfSupply}. One of them is wrong, and the GST split depends on it.`,
        });
    }
}
export const createCustomerSchema = partyBase
    .extend({
    /** Lets them see their invoices and pay online. Requires an email. */
    portalEnabled: z.boolean().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.portalEnabled && !v.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'Portal access needs an email address - that is how they get the link.',
        });
    }
    validateGstin(v, ctx);
});
// patchOf, not .partial(): an edit never fills in a create-time default (zod 4 - see zod-setup.ts).
export const updateCustomerSchema = patchOf(partyBase.extend({ portalEnabled: z.boolean().optional(), isActive: z.boolean().optional() }))
    .strict()
    .superRefine(validateGstin);
export const createVendorSchema = partyBase.strict().superRefine(validateGstin);
export const updateVendorSchema = patchOf(partyBase.extend({ isActive: z.boolean().optional() }))
    .strict()
    .superRefine(validateGstin);
export const listCustomersQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    /** See `LIST_STATUSES` in items.ts: a draft is listed only when asked for. */
    status: z.enum(LIST_STATUSES).default('active'),
    /** `receivable` lists only customers with an outstanding balance. */
    filter: z.enum(['all', 'receivable', 'overdue']).default('all'),
    sort: z.enum(['name', 'created', 'balance']).default('name'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    // The condition builder. `where`, because `filter` is taken above.
    ...advancedFilterQueryFragment,
})
    .strict();
//# sourceMappingURL=customers.js.map